import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useTrips } from '../../context/TripsContext';
import { useExpenses } from '../../context/ExpensesContext';
import { flightDetailsService, FlightDetails } from '../../services/supabase';
import { countriesService } from '../../services/countries.service';
import { cityLookupService } from '../../services/cityLookup.service';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plane, Hotel, Calendar, MapPin, Edit2, Save, X } from 'lucide-react';
import DateDisplay from '../../components/DateDisplay';
import LoadingAnimation from '../../components/LoadingAnimation';
import { FlightCountrySelector } from './components/FlightCountrySelector';

interface FlightItem {
  id: string;
  description: string;
  date: string;
  class: string;
  departureAirport: string;
  arrivalAirport: string;
  flightNumber: string;
  destinationCity: string;
  destinationCountry: string;
  cost: number;
  currency: string;
}

interface AccommodationItem {
  id: string;
  city: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  cost: number;
  currency: string;
  description: string;
}

// Helper function to extract date from flight description text
const extractDateFromText = (text: string, expenseDate?: string): string | null => {
  if (!text) return null;

  // Get year from expense date for patterns without year
  const yearFromExpense = expenseDate ? new Date(expenseDate).getFullYear() : new Date().getFullYear();

  // Common date patterns
  const patternsWithYear = [
    // ISO format: YYYY-MM-DD or YYYY/MM/DD
    /(\d{4})[-/](\d{1,2})[-/](\d{1,2})/,
    // US format: MM/DD/YYYY or MM-DD-YYYY
    /(\d{1,2})[-/](\d{1,2})[-/](\d{4})/,
    // Format with month names: Jan 15, 2024 or 15 Jan 2024
    /(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{4})/i,
    /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{1,2}),?\s+(\d{4})/i,
  ];

  // Patterns without year - we'll infer the year
  const patternsWithoutYear = [
    // Month name followed by day: "Oct 5", "October 5"
    /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{1,2})/i,
    // Day followed by month name: "5 Oct", "5 October"
    /(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*/i,
  ];

  // Try patterns with year first
  for (const pattern of patternsWithYear) {
    const match = text.match(pattern);
    if (match) {
      try {
        const dateStr = match[0];
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0]; // Return YYYY-MM-DD format
        }
      } catch (e) {
        continue;
      }
    }
  }

  // Try patterns without year, and append the year from expense date
  for (const pattern of patternsWithoutYear) {
    const match = text.match(pattern);
    if (match) {
      try {
        const dateStr = `${match[0]} ${yearFromExpense}`;
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0]; // Return YYYY-MM-DD format
        }
      } catch (e) {
        continue;
      }
    }
  }

  return null;
};

export const ItineraryPage = (): React.ReactNode => {
  const { currentTrip } = useTrips();
  const { expenses, loading, fetchTripExpenses } = useExpenses();
  const dateInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const [flightDetailsMap, setFlightDetailsMap] = useState<Record<string, FlightDetails>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<FlightItem>>({});
  const [countryNames, setCountryNames] = useState<string[]>([]);

  // Load countries list
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const countries = await countriesService.getCountryNames();
        setCountryNames(countries);
      } catch (error) {
        console.error('Error loading countries:', error);
      }
    };

    loadCountries();
  }, []);

  // Load expenses and flight details
  useEffect(() => {
    const loadData = async () => {
      if (currentTrip?.id) {
        await fetchTripExpenses(currentTrip.id);

        // Load flight details
        try {
          const flightDetails = await flightDetailsService.getByTripId(currentTrip.id);
          const detailsMap: Record<string, FlightDetails> = {};
          flightDetails.forEach(detail => {
            detailsMap[detail.expense_id] = detail;
          });
          setFlightDetailsMap(detailsMap);
        } catch (error) {
          console.error('Error loading flight details:', error);
        }
      }
    };

    loadData();
  }, [currentTrip?.id, fetchTripExpenses]);

  // Extract flights from expenses
  const flights = useMemo<FlightItem[]>(() => {
    const flightsData: FlightItem[] = [];

    expenses.forEach((expense) => {
      const category = expense.category?.toLowerCase() || '';

      if (category.includes('flight')) {
        const description = expense.description || 'Flight';
        const flightDetail = flightDetailsMap[expense.id];

        const flightClass = flightDetail?.flight_class || 'Economy';
        const flightDate = flightDetail?.actual_flight_date || extractDateFromText(description, expense.expense_date) || expense.expense_date;

        // Try to get destination country from flight_details, or extract from description
        let destinationCountry = flightDetail?.destination_country || '';

        // If destination country is not set, try to extract from description
        if (!destinationCountry) {
          const destinationCity = cityLookupService.extractDestinationCity(description);
          if (destinationCity) {
            // Note: getCityCountry is async, but we can't await in useMemo
            // We'll trigger the lookup asynchronously and it will be cached for next render
            cityLookupService.getCityCountry(destinationCity).then(country => {
              if (country && currentTrip?.id) {
                // Auto-save the discovered country to database
                flightDetailsService.upsert({
                  expense_id: expense.id,
                  trip_id: currentTrip.id,
                  destination_country: country,
                }).then(() => {
                  // Update local state to trigger re-render
                  setFlightDetailsMap(prev => ({
                    ...prev,
                    [expense.id]: {
                      ...prev[expense.id],
                      destination_country: country,
                    } as FlightDetails,
                  }));
                }).catch(error => {
                  console.error('Error auto-saving destination country:', error);
                });
              }
            });
          }
        }

        flightsData.push({
          id: expense.id,
          description,
          date: flightDate,
          class: flightClass,
          departureAirport: flightDetail?.departure_airport || '',
          arrivalAirport: flightDetail?.arrival_airport || '',
          flightNumber: flightDetail?.flight_number || '',
          destinationCity: flightDetail?.destination_city || '',
          destinationCountry,
          cost: expense.amount,
          currency: expense.currency || 'USD',
        });
      }
    });

    // Sort flights by date (earliest first)
    flightsData.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateA - dateB;
    });

    return flightsData;
  }, [expenses, flightDetailsMap, currentTrip?.id]);

  // Extract accommodations from expenses
  const accommodations = useMemo<AccommodationItem[]>(() => {
    const accommodationsData: AccommodationItem[] = [];

    expenses.forEach((expense) => {
      const category = expense.category?.toLowerCase() || '';

      if (category.includes('accommodation') || category.includes('lodging') || category.includes('hotel')) {
        accommodationsData.push({
          id: expense.id,
          city: 'N/A', // Could be enhanced with location data
          checkIn: expense.expense_date,
          checkOut: expense.expense_date, // Would need end date
          nights: 1, // Would need calculation
          cost: expense.amount,
          currency: expense.currency || 'USD',
          description: expense.description || 'Accommodation',
        });
      }
    });

    // Sort by check-in date
    accommodationsData.sort((a, b) => {
      const dateA = new Date(a.checkIn).getTime();
      const dateB = new Date(b.checkIn).getTime();
      return dateA - dateB;
    });

    return accommodationsData;
  }, [expenses]);

  // Start editing a flight
  const startEditing = (flight: FlightItem) => {
    setEditingId(flight.id);
    setEditForm({ ...flight });
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({});
  };

  // Save flight edits
  const saveFlightEdits = async () => {
    if (!editingId || !currentTrip?.id) return;

    try {
      // Update flight_details table (country is edited separately inline)
      await flightDetailsService.upsert({
        expense_id: editingId,
        trip_id: currentTrip.id,
        flight_class: editForm.class,
        actual_flight_date: editForm.date,
        departure_airport: editForm.departureAirport,
        arrival_airport: editForm.arrivalAirport,
        flight_number: editForm.flightNumber,
      });

      // Update local state
      setFlightDetailsMap(prev => ({
        ...prev,
        [editingId]: {
          ...prev[editingId],
          flight_class: editForm.class || null,
          actual_flight_date: editForm.date || null,
          departure_airport: editForm.departureAirport || null,
          arrival_airport: editForm.arrivalAirport || null,
          flight_number: editForm.flightNumber || null,
        } as FlightDetails,
      }));

      setEditingId(null);
      setEditForm({});
    } catch (error) {
      console.error('Error saving flight details:', error);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8 flex items-center justify-center min-h-[60vh]">
        <LoadingAnimation message="Loading your itinerary..." size="md" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-6xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Trip Itinerary</h1>
        <p className="text-muted-foreground">
          Review and edit your flight details and accommodations
        </p>
      </div>

      {/* Flights Section */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Plane className="h-6 w-6" />
            Flights
          </h2>
        </div>

        {flights.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              No flights added yet
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4 max-w-4xl">
            {flights.map((flight, index) => (
              <Card key={flight.id} className="group">
                <CardContent className="pt-6">
                  {editingId === flight.id ? (
                    // Edit mode
                    <div className="space-y-4">
                      <div className="text-sm text-muted-foreground mb-3">
                        Editing: {flight.description} - Edit date using the date picker in view mode
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium mb-1 block">Class</label>
                          <Select
                            value={editForm.class || 'Economy'}
                            onValueChange={(value) => setEditForm({ ...editForm, class: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Economy">Economy</SelectItem>
                              <SelectItem value="Premium Economy">Premium Economy</SelectItem>
                              <SelectItem value="Business">Business</SelectItem>
                              <SelectItem value="First Class">First Class</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-1 block">Departure Airport</label>
                          <Input
                            placeholder="e.g., TLV"
                            value={editForm.departureAirport || ''}
                            onChange={(e) => setEditForm({ ...editForm, departureAirport: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-1 block">Arrival Airport</label>
                          <Input
                            placeholder="e.g., ZRH"
                            value={editForm.arrivalAirport || ''}
                            onChange={(e) => setEditForm({ ...editForm, arrivalAirport: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-1 block">Flight Number</label>
                          <Input
                            placeholder="e.g., LX8396"
                            value={editForm.flightNumber || ''}
                            onChange={(e) => setEditForm({ ...editForm, flightNumber: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={saveFlightEdits} size="sm">
                          <Save className="h-4 w-4 mr-2" />
                          Save
                        </Button>
                        <Button onClick={cancelEditing} variant="outline" size="sm">
                          <X className="h-4 w-4 mr-2" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View mode
                    <div className="flex items-start justify-between gap-6">
                      <div className="flex gap-4 flex-1">
                        <div className="flex items-center justify-center bg-primary/10 rounded-full h-12 w-12 flex-shrink-0">
                          <Plane className="h-6 w-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-lg">{flight.description}</h3>
                            <span className="text-xs px-2 py-1 bg-secondary rounded-full">
                              {flight.class}
                            </span>
                          </div>
                          <div className="space-y-1 text-sm text-muted-foreground">
                            {(flight.departureAirport || flight.arrivalAirport) && (
                              <div className="flex items-center gap-2">
                                <MapPin className="h-4 w-4" />
                                <span>
                                  {flight.departureAirport || '?'} → {flight.arrivalAirport || '?'}
                                </span>
                              </div>
                            )}
                            {flight.flightNumber && (
                              <div className="text-sm">
                                Flight: {flight.flightNumber}
                              </div>
                            )}
                            <div className="font-medium text-foreground">
                              {flight.currency} {flight.cost.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Country selector in the middle */}
                      <FlightCountrySelector
                        country={flight.destinationCountry}
                        countryNames={countryNames}
                        isLastFlight={index === flights.length - 1}
                        onCountryChange={async (value) => {
                          if (currentTrip?.id) {
                            try {
                              await flightDetailsService.upsert({
                                expense_id: flight.id,
                                trip_id: currentTrip.id,
                                destination_country: value,
                              });
                              setFlightDetailsMap(prev => ({
                                ...prev,
                                [flight.id]: {
                                  ...prev[flight.id],
                                  destination_country: value,
                                } as FlightDetails,
                              }));
                            } catch (error) {
                              console.error('Error updating destination country:', error);
                            }
                          }
                        }}
                      />

                      {/* Date section on the right */}
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <DateDisplay
                            date={flight.date}
                            size="sm"
                            onClick={() => {
                              // Trigger the hidden date input
                              dateInputRefs.current[flight.id]?.showPicker?.();
                            }}
                          />
                          <input
                            ref={(el) => {
                              dateInputRefs.current[flight.id] = el;
                            }}
                            type="date"
                            value={flight.date}
                            onChange={async (e) => {
                              const newDate = e.target.value;
                              if (newDate && currentTrip?.id) {
                                try {
                                  await flightDetailsService.upsert({
                                    expense_id: flight.id,
                                    trip_id: currentTrip.id,
                                    actual_flight_date: newDate,
                                  });
                                  setFlightDetailsMap(prev => ({
                                    ...prev,
                                    [flight.id]: {
                                      ...prev[flight.id],
                                      actual_flight_date: newDate,
                                    } as FlightDetails,
                                  }));
                                } catch (error) {
                                  console.error('Error updating flight date:', error);
                                }
                              }
                            }}
                            className="absolute opacity-0 pointer-events-none w-0 h-0"
                            aria-hidden="true"
                          />
                        </div>
                        <Button
                          onClick={() => startEditing(flight)}
                          variant="ghost"
                          size="sm"
                          title="Edit other details"
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Accommodations Section */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Hotel className="h-6 w-6" />
            Accommodations
          </h2>
        </div>

        {accommodations.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              No accommodations added yet
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {accommodations.map((accommodation) => (
              <Card key={accommodation.id}>
                <CardContent className="pt-6">
                  <div className="flex gap-4">
                    <div className="flex items-center justify-center bg-primary/10 rounded-full h-12 w-12 flex-shrink-0">
                      <Hotel className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2">
                        {accommodation.description}
                      </h3>
                      <div className="space-y-1 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          <span>{accommodation.city}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          <span>
                            {formatDate(accommodation.checkIn)} - {formatDate(accommodation.checkOut)}
                          </span>
                          <span className="text-xs">
                            ({accommodation.nights} {accommodation.nights === 1 ? 'night' : 'nights'})
                          </span>
                        </div>
                        <div className="font-medium text-foreground">
                          {accommodation.currency} {accommodation.cost.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
